import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import classNames from "classnames";
import { bool, element, func, number, oneOf, oneOfType, shape, string } from "prop-types";
import { compose } from "redux";

import * as i18n from "util/i18n";
import * as configUtils from "util/config";
import { getNestedObject, getVariant } from "util/general";
import { getDevicePlatform } from "util/device";
import Button from "pages/_components/Button";

import FieldError from "pages/_components/fields/FieldError";
import FieldWarning from "pages/_components/fields/FieldWarning";
import FieldLabel from "pages/_components/fields/FieldLabel";
import { resizableRoute } from "pages/_components/Resizable";
import withFocus from "pages/_components/withFocus";
import FieldHint from "./FieldHint";

const TextField = ({ ...props }) => {
    const [hasWarning, setHasWarning] = useState(false);

    const onResize = () => {
        const { isDesktop, isFocused, mobileOS } = props;

        if (isFocused && !isDesktop && mobileOS === "Android") {
            /* eslint-disable-next-line react/no-find-dom-node */
            ReactDOM.findDOMNode(this).scrollIntoView({ block: "center", behavior: "smooth" });
        }
    };

    useEffect(() => {
        window.addEventListener("resize", onResize);
        return () => {
            window.removeEventListener("resize", onResize);
        };
    }, []);

    const callbackRef = useCallback((inputElement) => {
        if (!!props.autoFocus && inputElement) {
            setTimeout(() => {
                inputElement.focus();
                // document.getElementById("onboarding.step.verifyCUIT.cuit").focus();
            }, 500);
        }
    }, []);

    const handleBlur = (event) => {
        const { form, field, toggleIsFocused, onBlur, disableUseFieldOnBlur } = props;

        setHasWarning(false);
        onBlur(event);
        // Se deshabilito opcional porque en algunos casos causa error
        if (!disableUseFieldOnBlur) {
            field.onBlur(event);
            form.setFieldTouched(field.name);
        }
        toggleIsFocused();
    };

    const handleChange = (event) => {
        const {
            field,
            handleOnChange,
            maxLength,
            pattern,
            spaceEnabled,
            onlyNumbers,
            formatFunction,
            hasRegex,
            isDouble,
        } = props;
        const { onChange } = field;

        /* eslint no-control-regex: "off" */
        const reg = /[^\x00-\xFF]/g;

        const newEvent = event;
        if (formatFunction) {
            newEvent.target.value = formatFunction(event.target.value);
        }

        if (!reg.test(newEvent.target.value)) {
            if (!pattern || event.target.validity.valid) {
                if (newEvent.target.value.length > maxLength) {
                    return;
                }

                if (!spaceEnabled && newEvent.target.value.includes(" ")) {
                    return;
                }

                if (onlyNumbers && newEvent.target.value.length > maxLength) {
                    return;
                }

                const hasOnlyNumbers = new RegExp("^[0-9]*$");
                if (onlyNumbers && !hasOnlyNumbers.test(newEvent.target.value)) {
                    return;
                }

                const doubleValidation = configUtils.get("onboarding.double.validation", "^[0-9]*\\.?[0-9]*$");
                const valueIsDouble = new RegExp(doubleValidation);
                if (isDouble && !valueIsDouble.test(newEvent.target.value)) {
                    return;
                }

                const formatedRegex = hasRegex && new RegExp(hasRegex);
                if (hasRegex && !formatedRegex.test(newEvent.target.value)) {
                    return;
                }

                if (handleOnChange) {
                    handleOnChange(newEvent);
                }

                onChange(newEvent);
                setHasWarning(false);
                return;
            }
        }
        setHasWarning(true);
    };

    const handleOnCopy = (event) => {
        const { copyEnabled } = props;
        if (!copyEnabled) {
            event.preventDefault();
        }
    };

    // const renderLabel = () => {
    //     const { idForm, field, labelTextDefault, labelText, optional } = props;
    //     const key = labelText || `${idForm}.${field.name}.label`;
    //     return <FieldLabel labelKey={key} labelText={labelTextDefault} optional={optional} />;
    // };

    const renderPlaceholder = () => {
        const { idForm, field, hidePlaceholder, placeholderText } = props;
        if (hidePlaceholder) {
            return "";
        }
        if (placeholderText !== null) {
            return placeholderText;
        }
        return i18n.get(`${idForm}.${field.name}.placeholder`);
    };

    const customMinLengthValidation = ({ target }) => {
        const { minLength, idValidation, validationRegularExpresion } = props;

        const { value } = target;

        if (minLength) {
            if (value && value.length < minLength) {
                target.setCustomValidity(
                    i18n.get("generic.text.field.minLength.warning.message", null, {
                        MINLENGTH: minLength,
                        CURRENTLENGTH: value.length,
                    }),
                );
            } else {
                target.setCustomValidity("");
            }
        }

        if (idValidation && validationRegularExpresion) {
            const rexp = new RegExp(validationRegularExpresion);
            if (!rexp.test(value)) {
                switch (idValidation) {
                    case "email":
                        target.setCustomValidity(i18n.get("generic.text.field.email.validationError", null));
                        break;
                    default:
                        break;
                }
            } else {
                target.setCustomValidity("");
            }
        }
    };

    const {
        field,
        form: { touched, errors },
        inputFunctions,
        inputRef,
        hideLabel,
        hidePlaceholder,
        idForm,
        handleOnChange,
        isDesktop,
        isMobile,
        isMobileNative,
        mobileOS,
        toggleIsFocused,
        isFocused,
        renderAs: Element,
        autoCapitalize,
        mode,
        optional,
        nestedErrorsObject,
        warningMessage,
        labelText,
        labelTextDefault,
        placeholderText,
        classNameForViewMode,
        className,
        copyEnabled,
        idField,
        disabled,
        isIos,
        hintLabel,
        validationRegularExpresion,
        idValidation,
        autoFocus,
        labelnoI18n,
        showNumericKeyboard,
        labelFromService,
        renderAs,
        variant,
        showTooltip,
        onTooltipClick,
        ...otherProps
    } = props;

    const hasError = nestedErrorsObject
        ? getNestedObject(touched, field.name.split(".")) && getNestedObject(errors, field.name.split("."))
        : touched[field.name] && errors[field.name];

    if (mode !== "edit" && !field.value) {
        return null;
    }

    const fieldLabel = labelText || `${idForm}.${field.name}.label`;
    const id = idField || `${idForm}.${field.name}`;

    const showNumericKeyboardProps = {};
    if (showNumericKeyboard) {
        showNumericKeyboardProps.type = "number";
        if (getDevicePlatform() === "iOS") {
            showNumericKeyboardProps.pattern = "^d*$";
        }
    }

    const _className = getVariant(variant, renderAs);
    return (
        <div
            className={classNames("form-group", {
                "has-error": hasError,
                "has-focus": isFocused,
            })}>
            <div style={{ display: "flex" }}>
                {!hideLabel &&
                    (labelnoI18n !== null ? (
                        <FieldLabel labelText={labelnoI18n} optional={optional} idField={id} />
                    ) : (
                        <FieldLabel
                            labelKey={fieldLabel}
                            labelText={labelFromService ? labelText : labelTextDefault}
                            optional={optional}
                            idField={id}
                            className={className}
                        />
                    ))}

                {showTooltip && (
                    <Button
                        className="icon-button"
                        bsStyle="link"
                        image="images/figma/icon/info.svg"
                        block={false}
                        style={{ padding: "0 0 0 6px" }}
                        onClick={onTooltipClick}
                    />
                )}
            </div>
            {mode === "edit" ? (
                <div
                    className={`input-group${_className}${disabled ? " is-disabled" : ""} ${
                        isIos ? "is-disabled-ios" : ""
                    } ${className || ""}`}>
                    <Element
                        id={id}
                        className="form-control"
                        placeholder={renderPlaceholder()}
                        {...field}
                        {...otherProps}
                        {...showNumericKeyboardProps}
                        onFocus={toggleIsFocused}
                        onBlur={handleBlur}
                        ref={callbackRef}
                        onChange={handleChange}
                        autoCapitalize={autoCapitalize}
                        onCopy={handleOnCopy}
                        onCut={handleOnCopy}
                        onInvalid={customMinLengthValidation}
                        disabled={disabled}
                        hideLabel={hideLabel}
                    />
                    {inputFunctions}
                </div>
            ) : (
                <div className={classNameForViewMode}>{field.value}</div>
            )}
            {hasError && (
                <FieldError
                    error={nestedErrorsObject ? getNestedObject(errors, field.name.split(".")) : errors[field.name]}
                />
            )}
            {hintLabel && <FieldHint text={hintLabel} />}
            {hasWarning && <FieldWarning warning={warningMessage} />}
        </div>
    );
};

TextField.propTypes = {
    autoCapitalize: string,
    autoComplete: string,
    autoFocus: bool,
    field: shape({
        onBlur: func,
        onChange: func,
        name: string,
        value: oneOfType([number, string]),
    }).isRequired,
    form: shape({
        errors: shape({}),
        touched: shape({}),
    }).isRequired,
    handleOnChange: func,
    hideLabel: bool,
    hidePlaceholder: bool,
    inputFunctions: element,
    formatFunction: func,
    inputRef: oneOfType([func, shape({})]),
    isDesktop: bool.isRequired,
    isMobile: bool.isRequired,
    isMobileNative: bool.isRequired,
    maxLength: number,
    mobileOS: string.isRequired,
    mode: oneOf(["view", "edit"]),
    nestedErrorsObject: bool,
    optional: string,
    pattern: string,
    renderAs: string,
    type: string,
    warningMessage: string,
    labelText: string,
    labelTextDefault: string,
    placeholderText: string,
    isFocused: bool,
    toggleIsFocused: func,
    idForm: string,
    classNameForViewMode: string,
    className: string,
    copyEnabled: bool,
    spaceEnabled: bool,
    onlyNumbers: bool,
    idField: string,
    minLength: number,
    validationRegularExpresion: string,
    idValidation: string,
    onBlur: func,
    disabled: bool,
    isIos: bool,
    hintLabel: string,
    labelnoI18n: string,
    showNumericKeyboard: bool,
    labelFromService: bool,
    hasRegex: string,
    variant: string,
    isDouble: bool,
    showTooltip: bool,
    onTooltipClick: func,
    disableUseFieldOnBlur: bool,
};

TextField.defaultProps = {
    autoCapitalize: "sentences",
    autoComplete: "on",
    autoFocus: false,
    handleOnChange: null,
    hideLabel: false,
    hidePlaceholder: false,
    inputFunctions: null,
    formatFunction: null,
    inputRef: null, // React.createRef(),
    maxLength: 50,
    mode: "edit",
    nestedErrorsObject: false,
    optional: "",
    pattern: null,
    renderAs: "input",
    type: "text",
    warningMessage: "",
    labelText: null,
    labelTextDefault: null,
    placeholderText: null,
    isFocused: false,
    toggleIsFocused: null,
    idForm: "",
    classNameForViewMode: "",
    className: "",
    copyEnabled: true,
    spaceEnabled: true,
    onlyNumbers: false,
    idField: "",
    minLength: 0,
    validationRegularExpresion: "",
    idValidation: "",
    onBlur: () => {},
    disabled: false,
    isIos: false,
    hintLabel: null,
    labelnoI18n: null,
    showNumericKeyboard: false,
    labelFromService: false,
    hasRegex: "",
    variant: "",
    isDouble: false,
    showTooltip: false,
    onTooltipClick: () => {},
    disableUseFieldOnBlur: false,
};

export default compose(resizableRoute, withFocus)(TextField);
